import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * <cart-complete-the-look>
 *
 * Fetches complementary product recommendations from Shopify's recommendations
 * API based on the first item in the cart, and renders a horizontal scroller of
 * image-only product tiles. Refreshes when the cart changes.
 *
 * Attributes:
 *   data-product-id — Shopify product id used as the recommendations key.
 *   data-limit      — How many products to request (defaults to 6).
 */
class CartCompleteTheLook extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | null} */
  #lastFetchedKey = null;

  connectedCallback() {
    super.connectedCallback();

    this.#loadRecommendations();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#activeFetch?.abort();
  }

  #onCartUpdate = async () => {
    const cart = await this.#fetchCart();
    if (!cart) return;

    const firstItem = cart.items?.[0];
    if (!firstItem) {
      this.#renderEmpty();
      return;
    }

    if (String(firstItem.product_id) !== this.dataset.productId) {
      this.dataset.productId = String(firstItem.product_id);
      this.#loadRecommendations();
    }
  };

  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  async #loadRecommendations() {
    const productId = this.dataset.productId;
    if (!productId) {
      this.#renderEmpty();
      return;
    }

    const limit = Number(this.dataset.limit) || 6;
    const fetchKey = `${productId}:${limit}`;
    if (this.#lastFetchedKey === fetchKey) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const url = `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&intent=complementary&limit=${limit}`;

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this.#renderEmpty();
        return;
      }
      const data = await response.json();
      this.#lastFetchedKey = fetchKey;
      this.#renderProducts(data.products ?? []);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        this.#renderEmpty();
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {Array<{title: string, url: string, featured_image?: string, image?: string, price?: number}>} products
   */
  #renderProducts(products) {
    const track = this.querySelector('[ref="track"]');
    if (!(track instanceof HTMLElement)) return;

    if (products.length === 0) {
      this.#renderEmpty();
      return;
    }

    track.innerHTML = '';
    const fragment = document.createDocumentFragment();
    for (const product of products) {
      const image = product.featured_image || product.image || '';
      if (!image) continue;

      const tile = document.createElement('a');
      tile.className = 'cart-ctl__tile';
      tile.href = product.url;
      tile.setAttribute('aria-label', product.title);

      const img = document.createElement('img');
      img.src = this.#withImageSize(image, 320);
      img.alt = product.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.className = 'cart-ctl__tile-image';
      tile.appendChild(img);

      fragment.appendChild(tile);
    }

    track.appendChild(fragment);
    this.removeAttribute('hidden');
  }

  #renderEmpty() {
    const track = this.querySelector('[ref="track"]');
    if (track instanceof HTMLElement) track.innerHTML = '';
    this.setAttribute('hidden', '');
  }

  /**
   * @param {string} src
   * @param {number} width
   */
  #withImageSize(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  /** @param {Event} event */
  scrollTrack(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-direction]') : null;
    const direction = target?.getAttribute('data-direction');
    const track = this.querySelector('[ref="track"]');
    if (!(track instanceof HTMLElement) || !direction) return;

    const tile = track.querySelector('.cart-ctl__tile');
    const step = tile instanceof HTMLElement ? tile.offsetWidth + 8 : 120;
    track.scrollBy({ left: direction === 'next' ? step * 2 : -step * 2, behavior: 'smooth' });
  }
}

if (!customElements.get('cart-complete-the-look')) {
  customElements.define('cart-complete-the-look', CartCompleteTheLook);
}
